import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  List,
  ListItem,
  Tooltip,
  IconButton,
  Popper,
  Paper,
} from '@material-ui/core';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Info as InfoIcon,
} from '@material-ui/icons';
import styles from './PasswordConditions.module.scss';
import PropTypes from 'prop-types';

const PasswordConditions = (props) => {
  const {
    password = '',
    setCriteriaError,
    mode = '',
    enablePopup = false,
    anchorEl = null,
  } = props;

  const getErrors = () => {
    return {
      isMinLength: /^.{8,}$/.test(password),
      isMaxLength: /^.{0,128}$/.test(password),
      isUpperCase: /.*[A-Z].*/.test(password),
      isLowerCase: /.*[a-z].*/.test(password),
      isNumber: /.*[0-9].*/.test(password),
      isSpecialCharacter: /[!@#$%^&*.,~()_+\-=\[\]{};':"\\|<>\/?]+/.test(
        password,
      ),
    };
  };

  const [errors, setErrors] = useState(getErrors());

  const arrowRef = useRef(null);

  const getClassName = (condition) =>
    condition ? 'color_success' : 'color_error';

  const renderIcon = (condition) =>
    condition ? (
      <CheckIcon fontSize="small" className="mr-10" />
    ) : (
      <CloseIcon fontSize="small" className="mr-10" />
    );

  const specialCharInfo =
    '^ $ * . [ ] { } ( ) ? " ! @ # % & /  , > < \' : ; | _ ~ ` = + -';

  useEffect(() => {
    const fieldErrors = getErrors();
    let isError = false;

    Object.keys(fieldErrors).forEach((key) => {
      Boolean(!fieldErrors[key]) && (isError = true);
    });

    setErrors(fieldErrors);
    setCriteriaError(isError);
  }, [password]);

  const RenderList = () => {
    return (
      <List className={styles.criteriaList}>
        <ListItem className={getClassName(errors.isMinLength)}>
          {renderIcon(errors.isMinLength)}
          Minimum of 8 characters
        </ListItem>

        <ListItem className={getClassName(errors.isMaxLength)}>
          {renderIcon(errors.isMaxLength)}
          Maximum of 128 characters
        </ListItem>

        <ListItem className={getClassName(errors.isUpperCase)}>
          {renderIcon(errors.isUpperCase)}
          Contains uppercase
        </ListItem>

        <ListItem className={getClassName(errors.isLowerCase)}>
          {renderIcon(errors.isLowerCase)}
          Contains lowercase
        </ListItem>

        <ListItem className={getClassName(errors.isNumber)}>
          {renderIcon(errors.isNumber)}
          Contains number
        </ListItem>

        <ListItem className={getClassName(errors.isSpecialCharacter)}>
          {renderIcon(errors.isSpecialCharacter)}
          Contains special character
          {
            <Tooltip title={specialCharInfo}>
              <IconButton size="small">
                <InfoIcon
                  fontSize="small"
                  color="primary"
                  data-testid="heading-info-button"
                />
              </IconButton>
            </Tooltip>
          }
        </ListItem>
      </List>
    );
  };

  if (mode === 'popup') {
    return (
      <Popper
        modifiers={{
          arrow: {
            enabled: true,
            element: arrowRef.current,
          },
        }}
        style={{ zIndex: 1500 }}
        open={enablePopup}
        anchorEl={anchorEl}
        placement="top"
      >
        <Paper elevation={3} className={styles.popupPaperContainer}>
          <span
            ref={arrowRef}
            // This should be styles, class is not supported for arrow
            style={{
              position: 'absolute',
              width: 16,
              height: 16,
              backgroundColor: 'inherit',
              boxShadow: '1px 1px 1px rgba(0,0,0,0.05)',
              transform: 'rotate(45deg)',
              bottom: -8, // Pull the arrow below the Popper
              left: 'calc(50% - 8px)', // Center the arrow horizontally
              zIndex: 1,
            }}
          />
          <RenderList />
        </Paper>
      </Popper>
    );
  }

  return (
    <Box p={1}>
      <RenderList />
    </Box>
  );
};

PasswordConditions.propTypes = {
  password: PropTypes.string,
  setCriteriaError: PropTypes.func.isRequired,
  mode: PropTypes.string,
  enablePopup: PropTypes.bool,
  anchorEl: PropTypes.object,
};

export default PasswordConditions;
